import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  FiAlertCircle,
  FiBriefcase,
  FiEdit2,
  FiEye,
  FiHome,
  FiLayers,
  FiMapPin,
  FiPlus,
  FiTrash2,
  FiUsers,
} from 'react-icons/fi';

import BasePage from '../../../components/BasePage';
import DataTable, { Column, FilterField } from '../../../components/DataTable';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/useToast';

import {
  CompanyCell,
  CountChip,
  Field,
  FieldGroup,
  GhostButton,
  LocationCell,
  ModalBody,
  MutedCell,
  PageContainer,
  PageHeader,
  PrimaryButton,
  RowAction,
  SectionLabel,
  StatCard,
  StatsRow,
  SyndicateChip,
} from './styles';

interface ICity {
  id: string;
  name: string;
}

interface IComplaintLite {
  id: string;
  status?: number;
}

interface ICompany {
  id: string;
  nome_fantasia: string;
  razao_social?: string;
  cnpj?: string;
  state?: string;
  postal_code?: string;
  syndicate?: { id?: string; nome_fantasia?: string };
  city?: { nome?: string; uf?: string };
  cities?: ICity[];
  complaints?: IComplaintLite[];
}

interface ISyndicateInfo {
  id: string;
  nome_fantasia: string;
}

interface IIBGEState {
  uf: string;
  nome: string;
}

interface IIBGECity {
  id: number;
  nome: string;
}

interface ICreateCompanyValues {
  nome_fantasia: string;
  syndicate_id: string;
  razao_social: string;
  cnpj: string;
  postal_code: string;
  address: string;
  address2: string;
  state: string;
  city_id: string;
}

const cnpjMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const cepMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d)/, '$1-$2');
};

const initials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map(p => p[0])
    .join('')
    .toUpperCase();
};

const validationSchema = Yup.object().shape({
  nome_fantasia: Yup.string().required('O nome fantasia é obrigatório.'),
  razao_social: Yup.string(),
  cnpj: Yup.string(),
  postal_code: Yup.string(),
  address: Yup.string(),
  address2: Yup.string(),
  state: Yup.string(),
  city_id: Yup.string(),
});

const Companies: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});

  const [syndicates, setSyndicates] = useState<ISyndicateInfo[]>([]);
  const [states, setStates] = useState<IIBGEState[]>([]);
  const [modalCities, setModalCities] = useState<IIBGECity[]>([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Empresas - Viva Voz';
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<ICompany[]>('/companies', {
        params: { ...filterParams, page: currentPage },
      });
      setCompanies(response.data);
      setTotalCompanies(Number(response.headers['x-total-companies'] || 0));
    } finally {
      setLoading(false);
    }
  }, [filterParams, currentPage]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (user.role !== 'admin') return;
    api
      .get<ISyndicateInfo[]>('/unions')
      .then(res => setSyndicates(res.data))
      .catch(() => undefined);
  }, [user.role]);

  const handleLoadStates = useCallback(async () => {
    if (states.length > 0) return;
    const response = await api.get<IIBGEState[]>('/ibge/states');
    setStates(response.data);
  }, [states.length]);

  const handleLoadCities = useCallback(async (uf: string) => {
    if (!uf) {
      setModalCities([]);
      return;
    }
    const response = await api.get<IIBGECity[]>(`/ibge/cities/${uf}`);
    setModalCities(response.data);
  }, []);

  const handleOpenAdd = useCallback(async () => {
    await handleLoadStates();
    setShowAddModal(true);
  }, [handleLoadStates]);

  const handleOpenDelete = useCallback((id: string) => {
    setPendingDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!pendingDeleteId) return;
    try {
      await api.delete(`/companies/${pendingDeleteId}`);
      addToast({ title: 'Empresa removida', body: 'A empresa foi removida.' });
      setShowDeleteModal(false);
      setPendingDeleteId(null);
      loadCompanies();
    } catch {
      addToast({ title: 'Erro', body: 'Falha ao remover a empresa.' });
    }
  }, [pendingDeleteId, addToast, loadCompanies]);

  const handleCreate = useCallback(
    async (values: ICreateCompanyValues, reset: () => void) => {
      setSubmitting(true);
      try {
        const payload = {
          ...values,
          syndicate_id:
            user.role === 'admin'
              ? values.syndicate_id || null
              : user.syndicate_id,
        };
        await api.post('/companies', payload);
        addToast({
          title: 'Empresa criada',
          body: 'O cadastro foi salvo com sucesso.',
        });
        reset();
        setShowAddModal(false);
        loadCompanies();
      } catch {
        addToast({
          title: 'Erro',
          body: 'Não foi possível salvar a empresa.',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [user, addToast, loadCompanies],
  );

  const filters = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      {
        key: 'nome_fantasia',
        label: 'Nome fantasia',
        type: 'text',
        placeholder: 'Buscar empresa',
        span: user.role === 'admin' ? 4 : 6,
      },
    ];
    if (user.role === 'admin') {
      base.push({
        key: 'syndicate_id',
        label: 'Sindicato',
        type: 'select',
        placeholder: 'Todos',
        options: syndicates.map(s => ({
          value: s.id,
          label: s.nome_fantasia,
        })),
        span: 4,
      });
    }
    return base;
  }, [user.role, syndicates]);

  const aggregateStats = useMemo(() => {
    const totalCities = companies.reduce(
      (acc, c) => acc + (c.cities?.length || 0),
      0,
    );
    const totalComplaints = companies.reduce(
      (acc, c) => acc + (c.complaints?.length || 0),
      0,
    );
    const openComplaints = companies.reduce(
      (acc, c) =>
        acc +
        (c.complaints || []).filter(cp => cp.status === 1 || cp.status === 2)
          .length,
      0,
    );
    return { totalCities, totalComplaints, openComplaints };
  }, [companies]);

  const columns = useMemo<Column<ICompany>[]>(
    () => [
      {
        key: 'company',
        label: 'Empresa',
        render: company => (
          <CompanyCell>
            <span className="avatar">{initials(company.nome_fantasia)}</span>
            <div className="info">
              <span className="name" title={company.nome_fantasia}>
                {company.nome_fantasia}
              </span>
              {company.razao_social && (
                <span className="razao" title={company.razao_social}>
                  {company.razao_social}
                </span>
              )}
            </div>
          </CompanyCell>
        ),
      },
      {
        key: 'cnpj',
        label: 'CNPJ',
        width: '15%',
        render: c => <MutedCell>{c.cnpj || '—'}</MutedCell>,
      },
      {
        key: 'syndicate',
        label: 'Sindicato',
        width: '18%',
        render: c =>
          c.syndicate?.nome_fantasia ? (
            <SyndicateChip title={c.syndicate.nome_fantasia}>
              <FiHome size={12} />
              <span>{c.syndicate.nome_fantasia}</span>
            </SyndicateChip>
          ) : (
            <MutedCell>—</MutedCell>
          ),
      },
      {
        key: 'location',
        label: 'Localização',
        width: '15%',
        render: c =>
          c.city?.nome ? (
            <LocationCell>
              <FiMapPin size={12} />
              {c.city.nome}
              {c.state ? ` · ${c.state}` : ''}
            </LocationCell>
          ) : (
            <MutedCell>—</MutedCell>
          ),
      },
      {
        key: 'cities',
        label: 'Filiais',
        width: '8%',
        align: 'center',
        render: c => (
          <CountChip $tone="info">
            <FiLayers size={11} />
            {c.cities?.length || 0}
          </CountChip>
        ),
      },
      {
        key: 'complaints',
        label: 'Denúncias',
        width: '8%',
        align: 'center',
        render: c => (
          <CountChip $tone="brand">
            <FiAlertCircle size={11} />
            {c.complaints?.length || 0}
          </CountChip>
        ),
      },
    ],
    [],
  );

  const handleFiltersChange = useCallback((values: Record<string, string>) => {
    setFilterParams(values);
    setCurrentPage(1);
  }, []);

  return (
    <BasePage title="Empresas">
      <PageContainer>
        <PageHeader>
          <h1>Empresas</h1>
          <span className="subtitle">
            Gerencie as empresas vinculadas, filiais e o histórico de denúncias.
          </span>
        </PageHeader>

        <StatsRow>
          <StatCard $tone="brand">
            <span className="icon">
              <FiBriefcase size={20} />
            </span>
            <div className="body">
              <span className="label">Empresas cadastradas</span>
              <span className="value">{totalCompanies}</span>
              <span className="helper">no total</span>
            </div>
          </StatCard>

          <StatCard $tone="info">
            <span className="icon">
              <FiLayers size={20} />
            </span>
            <div className="body">
              <span className="label">Filiais</span>
              <span className="value">{aggregateStats.totalCities}</span>
              <span className="helper">na página atual</span>
            </div>
          </StatCard>

          <StatCard $tone="warning">
            <span className="icon">
              <FiAlertCircle size={20} />
            </span>
            <div className="body">
              <span className="label">Denúncias</span>
              <span className="value">{aggregateStats.totalComplaints}</span>
              <span className="helper">na página atual</span>
            </div>
          </StatCard>

          <StatCard $tone="neutral">
            <span className="icon">
              <FiUsers size={20} />
            </span>
            <div className="body">
              <span className="label">Em aberto</span>
              <span className="value">{aggregateStats.openComplaints}</span>
              <span className="helper">recebidas + análise</span>
            </div>
          </StatCard>
        </StatsRow>

        <DataTable<ICompany>
          title="Lista de empresas"
          subtitle="Filtre por nome ou sindicato e gerencie cadastros, filiais e denúncias."
          columns={columns}
          data={companies}
          rowKey="id"
          loading={loading}
          page={currentPage}
          pageSize={25}
          total={totalCompanies}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={(
            <PrimaryButton type="button" onClick={handleOpenAdd}>
              <FiPlus size={14} />
              Nova empresa
            </PrimaryButton>
          )}
          rowActions={row => (
            <>
              <RowAction
                type="button"
                onClick={() => history.push(`/settings/companies/${row.id}`)}
              >
                <FiEye size={12} />
                Detalhes
              </RowAction>
              <RowAction
                type="button"
                onClick={() => history.push(`/settings/companies/${row.id}`)}
              >
                <FiEdit2 size={12} />
                Editar
              </RowAction>
              <RowAction
                type="button"
                $danger
                onClick={() => handleOpenDelete(row.id)}
                aria-label="Remover empresa"
              >
                <FiTrash2 size={12} />
              </RowAction>
            </>
          )}
          emptyMessage="Nenhuma empresa encontrada."
        />
      </PageContainer>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Remover empresa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14,
              color: '#475569',
              margin: 0,
            }}
          >
            Tem certeza que deseja remover esta empresa? Essa ação não poderá
            ser desfeita.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <GhostButton type="button" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </GhostButton>
          <RowAction $danger type="button" onClick={handleDelete}>
            <FiTrash2 size={12} />
            Remover
          </RowAction>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        size="lg"
      >
        <Formik<ICreateCompanyValues>
          initialValues={{
            nome_fantasia: '',
            syndicate_id: '',
            razao_social: '',
            cnpj: '',
            postal_code: '',
            address: '',
            address2: '',
            state: '',
            city_id: '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, helpers) => handleCreate(values, () => helpers.resetForm())}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                  Nova empresa
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ModalBody as="form" onSubmit={handleSubmit}>
                  <SectionLabel>
                    <FiBriefcase size={11} />
                    Identificação
                  </SectionLabel>

                  <FieldGroup $cols={user.role === 'admin' ? 2 : 1}>
                    <Field>
                      <label htmlFor="nome_fantasia">
                        Nome fantasia *
                        <input
                          id="nome_fantasia"
                          name="nome_fantasia"
                          placeholder="Ex: Fazenda São João"
                          value={values.nome_fantasia}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.nome_fantasia && errors.nome_fantasia && (
                        <span className="error">{errors.nome_fantasia}</span>
                      )}
                    </Field>

                    {user.role === 'admin' && (
                      <Field>
                        <label htmlFor="syndicate_id">
                          Sindicato
                          <select
                            id="syndicate_id"
                            name="syndicate_id"
                            value={values.syndicate_id}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Selecione um sindicato</option>
                            {syndicates.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.nome_fantasia}
                              </option>
                            ))}
                          </select>
                        </label>
                      </Field>
                    )}
                  </FieldGroup>

                  <FieldGroup>
                    <Field>
                      <label htmlFor="razao_social">
                        Razão social
                        <input
                          id="razao_social"
                          name="razao_social"
                          placeholder="Ex: Fazenda São João LTDA"
                          value={values.razao_social}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </Field>

                    <Field>
                      <label htmlFor="cnpj">
                        CNPJ
                        <input
                          id="cnpj"
                          name="cnpj"
                          placeholder="00.000.000/0000-00"
                          value={values.cnpj}
                          maxLength={18}
                          onChange={e => setFieldValue('cnpj', cnpjMask(e.target.value))}
                          onBlur={handleBlur}
                        />
                      </label>
                    </Field>
                  </FieldGroup>

                  <SectionLabel>
                    <FiMapPin size={11} />
                    Endereço
                  </SectionLabel>

                  <FieldGroup $cols={3}>
                    <Field>
                      <label htmlFor="postal_code">
                        CEP
                        <input
                          id="postal_code"
                          name="postal_code"
                          placeholder="00000-000"
                          value={values.postal_code}
                          maxLength={9}
                          onChange={e => setFieldValue('postal_code', cepMask(e.target.value))}
                          onBlur={handleBlur}
                        />
                      </label>
                    </Field>
                    <Field>
                      <label htmlFor="state">
                        Estado
                        <select
                          id="state"
                          name="state"
                          value={values.state}
                          onChange={e => {
                            setFieldValue('state', e.target.value);
                            setFieldValue('city_id', '');
                            handleLoadCities(e.target.value);
                          }}
                          onBlur={handleBlur}
                        >
                          <option value="">UF</option>
                          {states.map(s => (
                            <option key={s.uf} value={s.uf}>
                              {s.uf}
                            </option>
                          ))}
                        </select>
                      </label>
                    </Field>
                    <Field>
                      <label htmlFor="city_id">
                        Município
                        <select
                          id="city_id"
                          name="city_id"
                          value={values.city_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!values.state}
                        >
                          <option value="">
                            {values.state
                              ? 'Selecione o município'
                              : 'Selecione o estado primeiro'}
                          </option>
                          {modalCities.map(city => (
                            <option key={city.id} value={city.id}>
                              {city.nome}
                            </option>
                          ))}
                        </select>
                      </label>
                    </Field>
                  </FieldGroup>

                  <FieldGroup>
                    <Field>
                      <label htmlFor="address">
                        Endereço
                        <input
                          id="address"
                          name="address"
                          placeholder="Rua, número"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </Field>

                    <Field>
                      <label htmlFor="address2">
                        Complemento
                        <input
                          id="address2"
                          name="address2"
                          placeholder="Bloco, sala..."
                          value={values.address2}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </Field>
                  </FieldGroup>
                </ModalBody>
              </Modal.Body>
              <Modal.Footer>
                <GhostButton
                  type="button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </GhostButton>
                <PrimaryButton
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  <FiPlus size={14} />
                  {submitting ? 'Salvando...' : 'Criar empresa'}
                </PrimaryButton>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>
    </BasePage>
  );
};

export default Companies;
